/** Civil-date helpers for the Morning List; dates are YYYY-MM-DD strings. */

const utc = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

export const daysBetween = (from: string, to: string): number =>
  Math.round((utc(to) - utc(from)) / 86400000);

export const addDays = (iso: string, n: number): string =>
  new Date(utc(iso) + n * 86400000).toISOString().slice(0, 10);

/** 0 = Sunday … 6 = Saturday. */
const weekday = (iso: string) => new Date(utc(iso)).getUTCDay();

/** Saturday coming (today when it is already the weekend). */
export const thisWeekend = (today: string): string => {
  const dow = weekday(today);
  return dow === 0 || dow === 6 ? today : addDays(today, 6 - dow);
};

/** Monday of next week. */
export const nextWeek = (today: string): string =>
  addDays(today, (8 - weekday(today)) % 7 || 7);

export const formatDay = (
  iso: string,
  language: string,
  options: Intl.DateTimeFormatOptions,
): string =>
  new Date(utc(iso)).toLocaleDateString(language || 'en-GB', {
    ...options,
    timeZone: 'UTC',
  });

/** "Today", "Tomorrow", "Friday" within the week, else "Fri 9 Oct". */
export const dueLabel = (
  due: string,
  today: string,
  language: string,
): string => {
  const offset = daysBetween(today, due);
  if (offset === 0) return 'Today';
  if (offset === 1) return 'Tomorrow';
  if (offset < 0) return lateLabel(due, today, language);
  if (offset < 7) return formatDay(due, language, { weekday: 'long' });
  return formatDay(due, language, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
};

/** "Since Thu" for the last week, else "Since 21 Sep". */
export const lateLabel = (
  due: string,
  today: string,
  language: string,
): string =>
  `Since ${formatDay(
    due,
    language,
    daysBetween(due, today) < 7
      ? { weekday: 'short' }
      : { day: 'numeric', month: 'short' },
  )}`;
